<script setup lang="ts">
import { BaseHeader, type BaseHeaderImage } from '@components/base';
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    headingHtml?: string;
    headingObjects?: BaseHeaderImage[] | string;
    ctaLabelHtml?: string;
    ctaFallbackText?: string;
    ctaHref?: string;
    ctaTarget?: string;
    ctaBg?: string | null;
    imageBg?: string | null;
    imageUrl?: string;
    imageSrcset?: string;
    imageAlt?: string;
    imageWidth?: number | null;
    imageHeight?: number | null;
  }>(),
  {
    headingHtml: '',
    headingObjects: () => [],
    ctaLabelHtml: '',
    ctaFallbackText: '',
    ctaHref: '',
    ctaTarget: '_self',
    ctaBg: '',
    imageBg: '',
    imageUrl: '',
    imageSrcset: '',
    imageAlt: '',
    imageWidth: null,
    imageHeight: null,
  },
);

function parseHeadingObjects(raw: BaseHeaderImage[] | string | undefined): BaseHeaderImage[] {
  if (raw === undefined || raw === null) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    let s = raw.trim();
    if (s === '' || s === '[]') return [];
    try {
      const parsed = JSON.parse(s) as unknown;
      return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
    } catch {
      if (s.includes('&quot;')) {
        s = s.replace(/&quot;/g, '"');
        try {
          const parsed = JSON.parse(s) as unknown;
          return Array.isArray(parsed) ? (parsed as BaseHeaderImage[]) : [];
        } catch {
          /* ignore */
        }
      }
      return [];
    }
  }
  return [];
}

const headingObjectsList = computed((): BaseHeaderImage[] => parseHeadingObjects(props.headingObjects));

const ctaLabelUsesHtml = computed(() => {
  const raw = props.ctaLabelHtml ?? '';
  return raw.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length > 0;
});

const imagePanelStyle = computed(() => {
  const bg = props.imageBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const ctaStyle = computed(() => {
  const bg = props.ctaBg?.trim();
  if (!bg) return undefined;
  return { backgroundColor: bg };
});

const show = computed(() => {
  const h = props.headingHtml ?? '';
  const headingText = h.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  const img = props.imageUrl?.trim() ?? '';
  const cta = props.ctaHref?.trim() ?? '';
  return headingText.length > 0 || img.length > 0 || cta.length > 0;
});

const introHeadingContentClass = 'text-left font-inter-tight font-semibold leading-125 tracking-tight text-off-black-500 text-30 lg:text-[40px]';
</script>

<template>
  <section
    v-if="show"
    class="hiw-first-section lg:py-72 py-40 font-inter md:py-56 lg:my-36 my-12"
  >
    <div class="container">
      <div class="grid grid-cols-1 lg:grid-cols-2 lg:items-center lg:gap-x-48 xl:gap-x-64">
        <BaseHeader
          v-if="headingHtml"
          as="div"
          class="order-1 lg:col-start-1 lg:row-start-1 lg:translate-y-1/2"
          :content="headingHtml"
          :objects="headingObjectsList"
          :content-class="introHeadingContentClass"
        />

        <div
          v-if="imageUrl"
          class="order-2 mx-auto my-24 w-full lg:flex lg:justify-end min-w-0 max-w-full lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:m-0 lg:max-w-full lg:self-center"
        >
          <div
            class="overflow-hidden rounded-3xl"
            :style="imagePanelStyle"
          >
            <div
              class="aspect-square w-full overflow-hidden lg:aspect-auto lg:h-[620px] lg:w-[540px]"
            >
              <img
                :src="imageUrl"
                :srcset="imageSrcset || undefined"
                :alt="imageAlt || ''"
                :width="imageWidth ?? undefined"
                :height="imageHeight ?? undefined"
                class="size-full object-cover object-center"
                loading="lazy"
                decoding="async"
              >
            </div>
          </div>
        </div>

        <a
          v-if="ctaHref"
          :href="ctaHref"
          :target="ctaTarget"
          class="hover:opacity-92 order-3 inline-flex items-center justify-center rounded-full px-32 py-20 lg:translate-y-1/4 lg:-mt-[50px] text-center text-14 font-medium leading-none text-white transition-opacity lg:col-start-1 lg:row-start-2 w-fit lg:max-w-none lg:justify-center lg:self-start lg:px-36"
          :class="{ 'bg-off-black-500': !ctaBg || !ctaBg.trim() }"
          :style="ctaStyle"
        >
          <span
            v-if="ctaLabelUsesHtml"
            class="[&_p]:m-0"
            v-html="ctaLabelHtml"
          />
          <span v-else>{{ ctaFallbackText }}</span>
        </a>
      </div>
    </div>
  </section>
</template>
